import { Controller, FieldValues, Path, useFormContext } from "react-hook-form";
import { ObrConfiguration } from "../../OBRSettings";
import * as React from "react";
import { useLanguage } from "../../Language";
import { Autocomplete, TextField } from "@mui/material";
import { BranchLimit, useBranchLimits } from "../../BranchLimits";

export interface HomeLocationProps<T extends FieldValues> {
  obrConfiguration: ObrConfiguration;
  name: Path<T>;
  required?: boolean;
}

// TODO default value isn't being set/registered
export const HomeLocation = <T extends FieldValues>({
  name,
  obrConfiguration,
  required,
}: HomeLocationProps<T>) => {
  const { translate } = useLanguage();
  const { visibleBranches } = useBranchLimits();
  const { control, formState, getFieldState } = useFormContext<T>();
  const { error } = getFieldState(name, formState);
  const labelsRef = React.useRef(new Map<number, BranchLimit["displayName"]>());
  const options: number[] = React.useMemo(() => {
    if (!visibleBranches) {
      return null;
    }

    const labels = labelsRef.current;
    labels.clear();

    return visibleBranches
      .filter(
        (branch) =>
          !(obrConfiguration.locationsToExclude || []).includes(
            parseInt(branch.identifier)
          )
      )
      .map(({ identifier, displayName }) => {
        const key = parseInt(identifier);
        labels.set(key, displayName);
        return key;
      });
  }, [obrConfiguration.locationsToExclude, visibleBranches]);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required:
          required &&
          translate("isRequired", {
            LABEL: translate("ci_desiredHomeLocation"),
          }),
      }}
      render={({ field: { onChange, ref, ...field } }) => (
        <Autocomplete
          sx={{ maxWidth: { sm: "320px" } }}
          {...field}
          onChange={(_, value) => onChange(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              required={required}
              error={!!error}
              helperText={error ? <>{error.message}</> : " "}
              label={translate("ci_desiredHomeLocation")}
              inputRef={ref}
            />
          )}
          options={options}
          getOptionLabel={(option) => labelsRef.current.get(option)}
        ></Autocomplete>
      )}
    />
  );
};
